import { Stack } from "@mui/material";
import { TransactionIdEntryForm } from "Components/Shared/Payments/TransactionIdEntryForm";
import {
  type TransactionIdEntryFormData,
  useTransactionIdEntryForm,
} from "Components/Shared/Payments/useTransactionIdEntryForm";
import { useNavigate } from "Navigation/useNavigate";
import { useSearchParams } from "Navigation/useSearchParams";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import type { ReactNode } from "react";

const RESOURCES = Resources.Customer.Pay.ManualEntry;

// Params worth carrying over to the payment screen. `step` and `error` are
// deliberately dropped — they describe a previous attempt, not the ID typed here.
const PRESERVED_SEARCH_PARAMS = ["campaign", "affiliateCode"] as const;

type Props = {
  message?: ReactNode;
};

// Shape-only validation, no lookup: submitting a well-formed but unknown ID
// behaves exactly like opening /pay/{id} directly.
export const AnonymousTransactionIdEntryForm = ({ message }: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { control, handleSubmit, isEmpty } = useTransactionIdEntryForm();

  const onSubmit = ({ transactionPublicID }: TransactionIdEntryFormData) => {
    const preservedEntries = PRESERVED_SEARCH_PARAMS.flatMap<[string, string]>(
      (key) => {
        const value = searchParams.get(key);
        return value ? [[key, value]] : [];
      },
    );
    const query = new URLSearchParams(preservedEntries).toString();
    const urlPath = `${encodeURIComponent(transactionPublicID)}${query ? `?${query}` : ""}`;

    navigate(`${appUrl("customer-payment-pay")}/${urlPath}`);
  };

  return (
    <Stack width="100%" maxWidth={400}>
      <TransactionIdEntryForm
        control={control}
        onSubmit={handleSubmit(onSubmit)}
        title={t(RESOURCES.ManualTitle)}
        message={message}
        isSubmitDisabled={isEmpty}
      />
    </Stack>
  );
};
